<template>
  <div>
    <div v-if="loaded">
      <TheDetailHeader
        :title="$t('pages.dashboard.rewardsPage.title')"
        :parent="{ label: $t('pages.dashboard.dashboardPage.crumbs'), to: { name: 'Dashboard' } }"
        :count="table.pagination.total ? { filtered: table.pagination.total, total: table.pagination.total } : null"
        unit-key="global.words.reward"
      />
      <div class="mb-6 px-6 sm:px-12">
        <ThePageSettings
          v-model:date-range-value="dateValue"
          display-store-selector
          display-date-range-picker
          @store-selected="fetchData"
          @update:date-range-value="dateRangeChanged"
        />
      </div>
      <div v-if="table.rows && table.rows.length > 0">
        <div class="mt-4 flex flex-col px-6 pb-6 sm:px-12 sm:pb-12">
          <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <AppTable
                ref="rewardsTable"
                :columns="translatedColumns"
                :rows="table.rows"
                :pagination="table.pagination"
                :sort="table.sort"
                @update:change-page="changePage"
                @update:change-sort="changeSort"
              >
                <template #td-customer="{ data }">
                  <RowCustomer
                    :id="data.customer.id"
                    :country-flag="data.customer.countryFlag ?? null"
                    :has-optin="data.customer.hasOptin"
                    :name="data.customer.name"
                    :phone="data.customer.phone"
                  />
                </template>
                <template #td-date="{ data }">
                  <div>
                    <div class="text-sm font-medium text-foreground">{{ data.date.date }}</div>
                    <div v-if="data.date.time" class="text-sm text-muted-foreground">
                      {{ data.date.time }}
                    </div>
                  </div>
                </template>
              </AppTable>
            </div>
          </div>
        </div>
      </div>
      <TheEmptyState
        v-else
        module="rewards"
        :title="$t(`pages.rewards.emptyState.title`)"
        :description="$t(`pages.rewards.emptyState.description`)"
      />
    </div>
    <TheLoadingPage v-else />
  </div>
</template>

<script>
import TheLoadingPage from '@/components/layout/TheLoadingPage.vue'
import TheEmptyState from '@/components/layout/TheEmptyState.vue'
import ThePageSettings from '@/components/layout/ThePageSettings.vue'
import useStore from '@/composables/store'

import dayjs from 'dayjs'

export default {
  components: {
    TheLoadingPage,
    TheEmptyState,
    ThePageSettings
  },
  setup() {
    const { selectedStore } = useStore()

    return {
      selectedStore
    }
  },
  data: () => {
    return {
      loaded: false,
      dateValue: [],
      table: {
        sort: {
          sortBy: 'date',
          sortOrder: 'DESC'
        },
        pagination: {
          currentPage: 1
        },
        columns: {
          customer: {
            id: 'customer',
            label: 'pages.dashboard.rewardsPage.tables.thead.customer'
          },
          date: {
            id: 'date',
            label: 'pages.dashboard.rewardsPage.tables.thead.date'
          },
          store: {
            id: 'store',
            label: 'pages.dashboard.rewardsPage.tables.thead.store'
          },
          reward: {
            id: 'reward',
            label: 'pages.dashboard.rewardsPage.tables.thead.reward'
          }
        }
      }
    }
  },
  computed: {
    translatedColumns() {
      return Object.fromEntries(
        Object.entries(this.table.columns).map(([key, col]) => [key, { ...col, label: this.$t(col.label) }])
      )
    },
    crumbs() {
      return [
        {
          name: this.$t('pages.dashboard.dashboardPage.crumbs'),
          route: 'Dashboard'
        },
        {
          name: this.$t('pages.dashboard.rewardsPage.title')
        }
      ]
    }
  },
  created() {
    this.dateValue = [
      this.$route.params?.fromDate ?? dayjs().subtract(6, 'day').format('DD-MM-YYYY'),
      this.$route.params?.toDate ?? dayjs().format('DD-MM-YYYY')
    ]
    this.fetchData()
  },
  methods: {
    fetchData() {
      this.axios
        .get('/rewards/filtered-claimed', {
          params: {
            pageSize: 10,
            page: this.table.pagination.currentPage,
            sortBy: this.table.sort.sortBy,
            sortOrder: this.table.sort.sortOrder,
            fromDate: this.dateValue[0],
            toDate: this.dateValue[1],
            storeIds: this.selectedStore
          }
        })
        .then((response) => {
          const rows = []
          this.loaded = true
          this.loaded = true
          response.data.data.forEach((row) => {
            let date = {}
            if (row.date === null) {
              date = {
                date: '-',
                time: ''
              }
            } else {
              date = {
                date: new Date(row.date).toLocaleDateString('fr-FR'),
                time: new Date(row.date).toLocaleTimeString('fr-FR')
              }
            }

            rows.push({
              id: row.customer.id,
              data: {
                customer: {
                  id: row.customer.id,
                  phone: row.customer.phone,
                  name: row.customer.firstName,
                  gender: row.customer.gender,
                  hasOptin: row.customer.hasOptin,
                  countryFlag: row.customer.country.flag
                },
                date: date,
                store: row.store.name,
                reward: row.reward.name
              }
            })
          })
          this.table.rows = rows
          this.table.pagination = {
            currentPage: response.data.meta.current_page,
            lastPage: response.data.meta.last_page,
            total: response.data.meta.total
          }
        })
    },
    changePage(page) {
      this.table.pagination.currentPage = page
      this.fetchData()
    },
    changeSort(sort) {
      this.table.sort = sort
      this.fetchData()
    },
    dateRangeChanged() {
      this.fetchData()
    }
  }
}
</script>
